import { COMPACT_MAX_WIDTH, slugify } from './dom.js';

const MEGA_ROOT_SELECTOR = '[data-bp-mega-runtime="v2"]';
const MEGA_SELECTOR = '[data-bp-mega]';
const PANEL_SELECTOR = '[data-bp-mega-panel]';
const TRIGGER_SELECTOR = '.hn-mn-link';
const ITEM_SELECTOR = '[data-bp-mega-item]';
const GROUP_SELECTOR = '[data-bp-mega-grp]';
const CHILDREN_ATTRIBUTE = 'data-bp-mega-children';
const READY_ATTRIBUTE = 'data-bp-mega-ready';
const READY_VALUE = 'true';
const OPEN_CLASS = 'is-open';
const SHOWN_CLASS = 'is-shown';
const SWAP_CLASS = 'is-swap';
const ON_CLASS = 'is-on';
const PANEL_ID_PREFIX = 'bp-mega-panel-';
const ACTIVE_CLASS = 'is-active';
const TRIGGER_OPEN_CLASS = 'is-mega-open';
const DEFAULT_GROUP = 'default';
// Intent delay for the in-panel cascade only; the nav triggers are click-operated.
const HOVER_INTENT_MS = 110;

// The published nav order: the panels are authored in a different order, so the positional fallback needs it.
const NAV_PANEL_ORDER = ['markets', 'products', 'services', 'sustainability', 'about-us', 'insights'];

// Panels whose markup ships with the dropdown switched off; the nav item stays a plain label and is skipped by the fallback below.
const DISABLED_PANELS = new Set(['insights']);

function panelKeyFor(trigger) {
  return slugify(trigger.textContent || '');
}

// A trigger claims its panel by label; localized navs fall back to position, and only when the nav and the panel set are the same size.
function resolveTriggerPanels(triggers, panels) {
  const resolved = new Map();
  const unresolved = [];

  for (const trigger of triggers) {
    const key = panelKeyFor(trigger);
    if (panels.has(key) && !DISABLED_PANELS.has(key)) resolved.set(trigger, key);
    else unresolved.push(trigger);
  }

  if (unresolved.length === 0 || triggers.length !== panels.size) return resolved;

  const claimed = new Set(resolved.values());
  for (const trigger of unresolved) {
    const key = NAV_PANEL_ORDER[triggers.indexOf(trigger)];
    if (!key || DISABLED_PANELS.has(key) || claimed.has(key) || !panels.has(key)) continue;
    resolved.set(trigger, key);
    claimed.add(key);
  }
  return resolved;
}

function focusableItems(scope) {
  return Array.from(scope.querySelectorAll(ITEM_SELECTOR)).filter(
    (item) => item.offsetParent !== null && (item.tagName === 'A' || item.hasAttribute('tabindex')),
  );
}

function initializeMegaMenu(root, documentObject, windowObject) {
  if (root.getAttribute(READY_ATTRIBUTE) === READY_VALUE) return false;

  const mega = root.querySelector(MEGA_SELECTOR);
  const triggers = Array.from(root.querySelectorAll(TRIGGER_SELECTOR));
  if (!mega || triggers.length === 0) return false;

  const panels = new Map();
  for (const panel of mega.querySelectorAll(PANEL_SELECTOR)) {
    panels.set(panel.getAttribute('data-bp-mega-panel'), panel);
  }
  if (panels.size === 0) return false;

  const triggerPanels = resolveTriggerPanels(triggers, panels);

  // Non-link cascade parents are inert without JS, so their affordances are added here; a childless, hrefless row stays out of the tab order.
  for (const item of mega.querySelectorAll(ITEM_SELECTOR)) {
    if (item.tagName === 'A' || !item.hasAttribute(CHILDREN_ATTRIBUTE)) continue;
    item.setAttribute('tabindex', '0');
    item.setAttribute('role', 'button');
  }

  let openKey = null;
  let path = [];
  let hoverTimer = 0;

  const clearTimers = () => {
    windowObject.clearTimeout(hoverTimer);
  };

  // A trigger that owns a panel is part of the menu: pointing or focusing one never closes what is open.
  const isWiredTrigger = (node) =>
    triggers.some((candidate) => triggerPanels.has(candidate) && (candidate === node || candidate.contains(node)));

  const render = () => {
    const panel = openKey ? panels.get(openKey) : null;
    if (!panel) return;

    const keys = path.map((step) => step.childKey).filter(Boolean);

    for (const item of panel.querySelectorAll(ITEM_SELECTOR)) {
      item.classList.remove(ACTIVE_CLASS);
      if (item.hasAttribute(CHILDREN_ATTRIBUTE)) {
        item.setAttribute('aria-expanded', String(keys.includes(item.getAttribute(CHILDREN_ATTRIBUTE))));
      }
    }
    for (const step of path) step.item.classList.add(ACTIVE_CLASS);

    for (const group of panel.querySelectorAll(GROUP_SELECTOR)) {
      const key = group.getAttribute('data-bp-mega-grp');
      const on = key === DEFAULT_GROUP ? keys.length === 0 : keys.includes(key);
      group.classList.toggle(ON_CLASS, on);
    }
  };

  const close = () => {
    clearTimers();
    if (!openKey) return;
    const panel = panels.get(openKey);
    if (panel) {
      panel.classList.remove(OPEN_CLASS, SHOWN_CLASS, SWAP_CLASS);
      for (const group of panel.querySelectorAll(GROUP_SELECTOR)) group.classList.remove(ON_CLASS);
      for (const item of panel.querySelectorAll(ITEM_SELECTOR)) {
        item.classList.remove(ACTIVE_CLASS);
        if (item.hasAttribute(CHILDREN_ATTRIBUTE)) item.setAttribute('aria-expanded', 'false');
      }
    }
    for (const trigger of triggers) {
      trigger.classList.remove(TRIGGER_OPEN_CLASS);
      if (triggerPanels.has(trigger)) trigger.setAttribute('aria-expanded', 'false');
    }
    mega.classList.remove(OPEN_CLASS);
    openKey = null;
    path = [];
  };

  const open = (key) => {
    if (windowObject.innerWidth <= COMPACT_MAX_WIDTH) return;
    const panel = panels.get(key);
    if (!panel || openKey === key) return;
    // Replacing an open panel is a content change, so a swap crossfades in place instead of re-running the entrance.
    const swapping = openKey !== null;
    close();

    openKey = key;
    path = [];
    mega.classList.add(OPEN_CLASS);
    panel.classList.add(OPEN_CLASS);
    if (swapping) panel.classList.add(SWAP_CLASS);
    for (const trigger of triggers) {
      const isOpen = triggerPanels.get(trigger) === key;
      trigger.classList.toggle(TRIGGER_OPEN_CLASS, isOpen);
      if (triggerPanels.has(trigger)) trigger.setAttribute('aria-expanded', String(isOpen));
    }
    render();
    windowObject.requestAnimationFrame(() => {
      if (openKey === key) panel.classList.add(SHOWN_CLASS);
    });
  };

  const levelOf = (item) => {
    const group = item.closest(GROUP_SELECTOR);
    if (!group) return 1;
    const index = path.findIndex((step) => step.childKey === group.getAttribute('data-bp-mega-grp'));
    return index === -1 ? 1 : index + 2;
  };

  const activate = (item) => {
    if (!openKey) return;
    const level = levelOf(item);
    path = path.slice(0, level - 1);
    path.push({ item, childKey: item.getAttribute(CHILDREN_ATTRIBUTE) });
    render();
  };

  for (const trigger of triggers) {
    const key = triggerPanels.get(trigger);
    if (!key) continue;

    trigger.setAttribute('aria-expanded', 'false');
    // Without this the panel is an anonymous box in the accessibility tree: it gets a stable id, a region role, and its nav item's name.
    const panelId = `${PANEL_ID_PREFIX}${key}`;
    const panel = panels.get(key);
    panel.setAttribute('id', panelId);
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-label', `${(trigger.textContent || '').trim()} menu`);
    trigger.setAttribute('aria-controls', panelId);
    trigger.setAttribute('aria-haspopup', 'true');

    // The trigger is an anchor, so the click has to be swallowed: it operates the menu rather than navigating.
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      if (openKey === key) close();
      else open(key);
    });
    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (openKey === key) close();
        else open(key);
        return;
      }
      if (event.key !== 'ArrowDown' || openKey !== key) return;
      event.preventDefault();
      const first = focusableItems(panels.get(key))[0];
      if (first) first.focus();
    });
  }

  mega.addEventListener('pointerover', (event) => {
    const item = event.target.closest?.(ITEM_SELECTOR);
    if (!item || !mega.contains(item)) return;
    clearTimers();
    hoverTimer = windowObject.setTimeout(() => activate(item), HOVER_INTENT_MS);
  });

  mega.addEventListener('focusin', (event) => {
    const item = event.target.closest?.(ITEM_SELECTOR);
    if (item) activate(item);
  });

  mega.addEventListener('keydown', (event) => {
    const item = event.target.closest?.(ITEM_SELECTOR);
    if (!item) return;

    if ((event.key === 'Enter' || event.key === ' ') && item.tagName !== 'A') {
      event.preventDefault();
      activate(item);
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return;
    }
    event.preventDefault();

    const column = item.closest(GROUP_SELECTOR) || item.parentElement;
    const siblings = focusableItems(column);
    const index = siblings.indexOf(item);

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const next = siblings[index + (event.key === 'ArrowDown' ? 1 : -1)];
      if (next) next.focus();
      return;
    }

    if (event.key === 'ArrowRight') {
      activate(item);
      const childKey = item.getAttribute(CHILDREN_ATTRIBUTE);
      if (!childKey) return;
      const group = panels.get(openKey)?.querySelector(`[data-bp-mega-grp="${childKey}"]`);
      const first = group ? focusableItems(group)[0] : null;
      if (first) first.focus();
      return;
    }

    const previous = path[levelOf(item) - 2];
    if (previous) previous.item.focus();
  });

  documentObject.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !openKey) return;
    const key = openKey;
    close();
    const trigger = triggers.find((candidate) => triggerPanels.get(candidate) === key);
    if (trigger) trigger.focus();
  });

  // Capture phase: outside click is a primary close path, and controls that stop their click bubbling (the rail arrows) must still reach it.
  documentObject.addEventListener('click', (event) => {
    if (!openKey || root.contains(event.target)) return;
    close();
  }, { capture: true });

  documentObject.addEventListener('focusin', (event) => {
    if (!openKey) return;
    const target = event.target;
    if (!root.contains(target)) {
      close();
      return;
    }
    // Focus landing on another header control closes the panel; only the panel itself and the menu's own triggers keep it open.
    if (mega.contains(target) || isWiredTrigger(target)) return;
    close();
  });

  windowObject.addEventListener('resize', () => {
    if (windowObject.innerWidth <= COMPACT_MAX_WIDTH) close();
  });

  root.setAttribute(READY_ATTRIBUTE, READY_VALUE);
  return true;
}

export function initializeMegaMenus(root = document, environment = {}) {
  const documentObject = environment.document ?? root.ownerDocument ?? globalThis.document;
  const windowObject = environment.window ?? documentObject?.defaultView ?? globalThis.window;
  if (!root || !documentObject || !windowObject) return 0;

  if (root.matches?.(MEGA_ROOT_SELECTOR)) {
    return initializeMegaMenu(root, documentObject, windowObject) ? 1 : 0;
  }

  let initialized = 0;
  for (const megaRoot of root.querySelectorAll?.(MEGA_ROOT_SELECTOR) ?? []) {
    if (initializeMegaMenu(megaRoot, documentObject, windowObject)) initialized += 1;
  }
  return initialized;
}
